<template>
  <sortable-list
    :sortable="sortable"
    :items="items"
    :item-key="itemKey"
    :order="itemOrder || undefined"
    :line-class="lineClass ?? 'line'"
    :direction="direction"
    :selected="selectedKeys"
    @update:order="$emit('update:order', $event)"
    @contextmenu.stop.prevent="onContextMenu(null, $event)"
  >
    <template #default="{ item, index, orderedItems }">
      <button-item
        :class="[buttonClass]"
        :selected="isSelected(item)"
        :icons="actionIcons"
        :disabled="disabledIds.includes(item[itemKey])"
        @click:x="onRemoveClick($event, item)"
        @click:trash-2="onDeleteClick($event, item)"
        @click="onClick(item, $event, orderedItems)"
        @pointerover="onMouseOver(item)"
        @pointerout="onMouseOut(item)"
        @contextmenu.stop.prevent="onContextMenu(item, $event)"
      >
        <slot :item="item" :index="index">
          <template v-if="!prop">
            {{ item }}
          </template>
          <template v-else>
            {{ item[prop] }}
          </template>
        </slot>
        <template #after-text>
          <slot name="after-text" :item="item"></slot>
        </template>
        <template v-if="$slots.icons != null" #icons>
          <slot name="icons" :item="item"></slot>
        </template>
      </button-item>
    </template>

    <template #placeholder>
      <slot name="placeholder"></slot>
    </template>

    <template #before="{ item, index }">
      <slot name="before" :item="item" :index="index" />
    </template>
    <template #after="{ item, index }">
      <slot name="after" :item="item" :index="index" />
    </template>

    <template #after-list>
      <slot name="after-list"></slot>
    </template>
  </sortable-list>
</template>

<script
  lang="ts"
  setup
  generic="
    K extends string | number,
    T extends ({ [P in IK]: K } & { type?: string }),
    IK extends string = 'id',
    MS extends boolean = false
  "
>
import { computed, ref } from "vue";

import SortableList from "@core/ui/SortableList.vue";
import ButtonItem from "@core/input/ButtonItem.vue";

import type { Handleable } from "@core/types/Events";
import { isObject } from "lodash-es";
import { notNull } from "@/helpers/utility/Type";

interface Props {
  items: { [key: number]: T } | { [key: string]: T };
  itemKey?: IK;
  selectedItem?: Partial<T> | null;
  selectedItems?: T[];
  deletable?: boolean;
  removable?: boolean;
  buttonClass?: string | Record<string, boolean> | null;
  lineClass?: string | Record<string, boolean> | null;
  prop?: keyof T | null;
  order?: T[IK][];
  sortable?: boolean;
  direction?: "horizontal" | "vertical";
  disabledIds?: (string | number)[];
  multiSelect?: MS;
}
const {
  items,
  itemKey = "id" as IK,
  selectedItem = null,
  selectedItems = [],
  deletable = false,
  removable = false,
  buttonClass = null,
  lineClass = null,
  prop = null,
  order = undefined,
  sortable = false,
  direction = "vertical",
  disabledIds = [],
  multiSelect = false,
} = defineProps<Props>();

type ContextMenuEvent<M extends boolean = MS> = MouseEvent & (M extends true ? { items: T[] } : { item: T | null });

const emit = defineEmits<{
  (e: "contextmenu", event: ContextMenuEvent): void;
  (e: "select", item: T): void;
  (e: "select-many", items: T[]): void;
  (e: "remove", item: T): void;
  (e: "delete", item: T): void;
  (e: "hoveron", item: T): void;
  (e: "hoveroff", item: T): void;
  (e: "update:order", order: T[IK][]): void;
}>();

const actionIcons = computed(() => {
  const icons: ("x" | "trash-2")[] = [];
  if (removable) icons.push("x");
  if (deletable) icons.push("trash-2");
  return icons;
});

const selectedId = computed(() => {
  if (selectedItem == null) return null;
  if (!isObject(selectedItem)) return null;
  return selectedItem[itemKey];
});

const selectedType = computed(() => {
  if (selectedItem == null) return null;
  if (typeof selectedItem === "number") return null;
  return selectedItem?.type;
});

const selectedKeys = computed(() => {
  return selectedItems
    .map((item): T[IK] | null => {
      if (!isObject(item)) return null;
      return item[itemKey];
    })
    .filter(notNull);
});

const itemOrder = computed((): T[IK][] | null => {
  if (order) return order;
  return null;
});

function isSelected(item: T) {
  if (item == null) return false;

  if (!isObject(item)) {
    return item === (selectedItem as T);
  }

  const itemNotNumber = item as Exclude<T, number>;

  return (
    (selectedId.value === itemNotNumber?.[itemKey] && selectedType.value === item.type) ||
    selectedItems.find(
      (i) => isNotNumber(i) && i.type === item.type && i?.[itemKey] === itemNotNumber?.[itemKey]
    ) != null
  );
}

const lastSelectedItem = ref<T | null>(null);

function onClick(item: T, mouseEvent: MouseEvent & Handleable, orderedItems: T[]) {
  if (mouseEvent.hasBeenHandled === true) return;
  mouseEvent.hasBeenHandled = true;
  if (multiSelect) {
    if (mouseEvent.ctrlKey || mouseEvent.metaKey) {
      if (isSelected(item)) {
        const newSelection = selectedItems.filter((i) => {
          if (isNumber(i) || isNumber(item)) return i !== item;
          return i[itemKey] !== item[itemKey] || i.type !== item.type;
        });
        emit("select-many", newSelection);
      } else {
        emit("select-many", [...selectedItems, item]);
      }
    } else if (mouseEvent.shiftKey && selectedItems.length > 0) {
      const lastSelected = lastSelectedItem.value;
      const lastIndex = findItemIndex(orderedItems, lastSelected);
      const itemIndex = findItemIndex(orderedItems, item);
      if (itemIndex === -1 || lastIndex === -1) {
        lastSelectedItem.value = item;
        emit("select", item);
        return;
      }
      // Select range
      const [start, end] = itemIndex < lastIndex ? [itemIndex, lastIndex] : [lastIndex, itemIndex];
      const newSelection = orderedItems.slice(start, end + 1);
      emit("select-many", newSelection);
    } else {
      lastSelectedItem.value = item;
      emit("select", item);
    }
  } else {
    lastSelectedItem.value = item;
    emit("select", item);
  }
}

function onRemoveClick(mouseEvent: MouseEvent & Handleable, item: T) {
  mouseEvent.hasBeenHandled = true;
  emit("remove", item);
}

function onDeleteClick(mouseEvent: MouseEvent & Handleable, item: T) {
  mouseEvent.hasBeenHandled = true;
  emit("delete", item);
}

function onMouseOver(item: T) {
  emit("hoveron", item);
}

function onMouseOut(item: T) {
  emit("hoveroff", item);
}

function onContextMenu(item: T | null, event: MouseEvent & { item?: T | null }) {
  if (multiSelect) {
    const ev = event as ContextMenuEvent<true>;
    if (selectedItems) {
      // If the item is not in the selected items, open context menu for that item only
      if (!selectedItems.find((i) => {
        if (isNumber(i) || item == null || isNumber(item)) return false;
        return i[itemKey] === item[itemKey] && i.type === item.type;
      })) {
        ev.items = item != null ? [item] : [];
        emit("contextmenu", ev as unknown as ContextMenuEvent);
        return;
      }

      ev.items = selectedItems.map((i): T => {
        if (isNumber(i)) return i;
        if (itemKey in i) return findItem(items, i[itemKey]);
        return i;
      });
    } else {
      if (event.item) {
        ev.items = [event.item];
      } else {
        ev.items = [];
      }
    }
    emit("contextmenu", ev as unknown as ContextMenuEvent);
  } else {
    event.item = item;
    emit("contextmenu", event as ContextMenuEvent);
  }
}

// =======================================
// UTILS

function findItem(array: { [key: number]: T } | { [key: string]: T }, key: K): T {
  const arr = Object.values(array);
  const found = arr.find((i) => {
    if (isNumber(i)) {
      return i === (key as number);
    }
    return i[itemKey] === key;
  });
  if (found == null) throw new Error("Item not found in array");
  return found;
}

function findItemIndex(array: T[], item: T): number {
  return array.findIndex((i) => {
    if (isNotNumber(i) && isNotNumber(item)) {
      return i[itemKey] === item[itemKey] && i.type === item.type;
    }
    return i === item;
  });
}

function isNotNumber(item: T): item is Exclude<T, number> {
  return typeof item !== "number";
}
function isNumber(item: T): item is Extract<T, number> {
  return typeof item === "number";
}
</script>

<style lang="scss"></style>
